import React, { useState } from "react";
import { notification } from "antd";

import TransactionList from "./TransactionList";
import { Transaction } from "../../../../types/transactions";
import { useMatchDocumentToTransactionMutation } from "../../../../api/graphql/mutations/matchEmailDocumentToTransaction";
import { EmailDocument } from "../../../../api/graphql";
import {
  destroyMessage,
  showGraphQlErrorNotification,
  showLoadingMessage,
  showMessage,
} from "../../../../utils";
import { IMessageType } from "../../../../types";
import {
  UPSERT_KONTAX_TRANSACTIONS_MUTATION,
  useUpsertTransactionsMutation,
} from "../../../../api/graphql/mutations/transaction/upsertKontaxTransactions";

const matchingErrorMessage =
  "Error during matching transaction with email document";
const loadingKey = "matching-document-to-transaction";

const TransactionListContainer = ({
  transactions,
  email,
  currentEmailDocument,
  onMatchSuccess,
}: {
  transactions: Transaction[];
  email: string;
  currentEmailDocument: EmailDocument;
  onMatchSuccess: () => void;
}) => {
  const [isLoading, setIsLoading] = useState(false);

  /*
   * Always take the customer from the receipt on screen, never from the `email`
   * URL parameter: that parameter is written after the receipt renders, so it
   * still names the previous customer while the new receipt is already visible
   * (SMT-1320). The parameter stays as a fallback for the rare document without
   * a `userEmail`.
   */
  const customerEmail = currentEmailDocument.userEmail ?? email;

  const [matchDocumentToTransactionMutation] =
    useMatchDocumentToTransactionMutation();

  const [upsertKontaxTransactions] = useUpsertTransactionsMutation({
    refetchQueries: [UPSERT_KONTAX_TRANSACTIONS_MUTATION],
  });

  // A successful response with `success: false` means the match was rejected
  // because a copy of this receipt from another source is already linked to a
  // transaction (the backend's cross-provider dedup). That is an expected
  // outcome, not an error — show a clear notice instead of the generic error.
  const showAlreadyMatchedNotice = () => {
    notification.info({
      message: "Receipt already linked to a transaction",
      description:
        "A copy of this receipt from another source is already linked to a transaction, so it was not matched again.",
    });
  };

  const handleMatchSuccess = async () => {
    showMessage({
      key: loadingKey,
      type: IMessageType.SUCCESS,
      content: "Beleg successfully matched with Transaction",
    });
    await new Promise((resolve) => setTimeout(resolve, 2000));
    await onMatchSuccess();
  };

  const matchDocumentToTransaction = async (
    transactionId: string,
    skipStartLoading?: boolean
  ) => {
    if (!skipStartLoading) {
      if (isLoading) {
        return;
      }

      setIsLoading(true);
      showLoadingMessage(loadingKey);
    }

    try {
      const result = await matchDocumentToTransactionMutation({
        variables: {
          email: customerEmail,
          emailDocumentId: currentEmailDocument.id,
          transactionId,
        },
      });
      const matchResult = result.data?.matchEmailDocumentToTransaction;
      if (matchResult?.success === true) {
        await handleMatchSuccess();
      } else if (matchResult?.success === false) {
        showAlreadyMatchedNotice();
      } else {
        // Only an explicit `success: false` is the cross-provider dedup
        // rejection. A null/undefined result is an unexpected response, not an
        // "already matched" case — surface it as an error.
        showGraphQlErrorNotification(
          matchingErrorMessage,
          new Error("Unexpected match response")
        );
      }
    } catch (error) {
      showGraphQlErrorNotification(matchingErrorMessage, error);
    } finally {
      setIsLoading(false);
      destroyMessage(loadingKey);
    }
  };

  const bookAndMatchTransaction = async (
    transaction: Transaction,
    vatCategoryCode: string,
    categoryCode: string
  ) => {
    if (isLoading) {
      return;
    }

    if (transaction.verified) {
      showMessage({
        key: loadingKey,
        type: IMessageType.ERROR,
        content: "It is not allowed to recategorize verified transactions",
      });
      return;
    }
    setIsLoading(true);
    showLoadingMessage(loadingKey);
    try {
      await upsertKontaxTransactions({
        variables: {
          transactions: [
            {
              kontistTransactionId: transaction.id,
              vatCategoryCode,
              categoryCode,
            },
          ],
        },
      });
    } catch (error) {
      showGraphQlErrorNotification(
        "Error during updating transaction categories",
        error
      );
      setIsLoading(false);
      destroyMessage(loadingKey);
      return;
    }

    await matchDocumentToTransaction(transaction.id, true);
  };

  return (
    <TransactionList
      transactions={transactions}
      matchDocumentToTransaction={matchDocumentToTransaction}
      bookAndMatchTransaction={bookAndMatchTransaction}
      customerEmail={customerEmail}
      receiptId={currentEmailDocument.id}
      prefillAmount={currentEmailDocument.amount}
    />
  );
};

export default TransactionListContainer;
